import { LegalLinks } from '../LegalDocsPage';
import useSupportFaq from '../client/useSupportFaq';
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Stack,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  FormControlLabel,
  Switch,
} from "@mui/material";
import StorefrontIcon from "@mui/icons-material/Storefront";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import CreditCardOffIcon from "@mui/icons-material/CreditCardOff";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import PlaceIcon from "@mui/icons-material/Place";
import CloseIcon from "@mui/icons-material/Close";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import SearchIcon from "@mui/icons-material/Search";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import CheckIcon from "@mui/icons-material/Check";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import ScheduleIcon from "@mui/icons-material/Schedule";
import { useNavigate, useSearchParams, Link as RouterLink } from "react-router-dom";
import api from "../api/api";
import "./StorefrontPage.css";

// Общий акцент публичной витрины.
const BRAND_GRADIENT = "#a5652c";
const TERM_OPTIONS = [1, 2, 3, 4, 6, 8, 12];

// Палитра для категорий — назначается детерминированно по названию,
// чтобы у одной категории всегда был один и тот же цвет.
const CATEGORY_COLORS = ["#a5652c", "#566578", "#776440", "#386866", "#625a72", "#725344"];
function colorForCategory(name) {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return CATEGORY_COLORS[Math.abs(hash) % CATEGORY_COLORS.length];
}

function groupByCategory(items) {
  const groups = Object.create(null);
  for (const item of items) {
    const key = item.category || "Другое";
    if (!groups[key]) groups[key] = [];
    groups[key].push(item);
  }
  return groups;
}

function matchesQuery(item, query) {
  if (!query) return true;
  const haystack = `${item.name} ${item.description || ""} ${item.category || ""}`.toLowerCase();
  return haystack.includes(query);
}

// Главная страница ПрокатПрост: витрина-каталог для клиентов.
// Вход для сотрудников нарочно не выносится в шапку — только мелкой ссылкой в футере.

/* ── Reveal-on-scroll: появление блока при попадании во вьюпорт ── */
function Reveal({ children, variant = "up", delay = 0, style, ...rest }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(node);
        }
      },
      { threshold: 0.15, rootMargin: "0px 0px -60px 0px" }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const variantClass = variant !== "up" ? `sf-reveal--${variant}` : "";
  return (
    <div
      ref={ref}
      className={`sf-reveal ${variantClass} ${visible ? "sf-reveal--visible" : ""}`.trim()}
      style={{ transitionDelay: `${delay}ms`, ...style }}
      {...rest}
    >
      {children}
    </div>
  );
}

function getImageUrls(images) {
  if (!images) return [];
  const values = Array.isArray(images) ? images : typeof images === "object" ? Object.values(images) : [];
  return values
    .map((value) => (typeof value === "string" ? value : value?.url))
    .filter(Boolean);
}

const HOW_IT_WORKS = [
  {
    icon: <StorefrontIcon sx={{ fontSize: 32 }} />,
    title: "Выберите товар и пункт проката",
    text: "Смотрите, что есть в наличии рядом с вами — по каждому пункту своя витрина.",
  },
  {
    icon: <CreditCardOffIcon sx={{ fontSize: 32 }} />,
    title: "Оформите аренду без залога",
    text: "Оплата картой онлайн. Никаких залоговых сумм — только стоимость аренды.",
  },
  {
    icon: <PhoneIphoneIcon sx={{ fontSize: 32 }} />,
    title: "Подтвердите личность в приложении",
    text: "Вход по телефону, привязка карты — это и заменяет залог.",
  },
  {
    icon: <LockOpenIcon sx={{ fontSize: 32 }} />,
    title: "Заберите и пользуйтесь",
    text: "Заберите вещь в выбранном пункте. Адрес и порядок получения уточняются при оформлении.",
  },
];

/* ── Компактная карточка товара для карусели/модалки ── */
function ProductMiniCard({ item, onRent, cartQuantity = 0, fullWidth = false }) {
  const imageUrls = getImageUrls(item.images);
  const [imageIndex, setImageIndex] = useState(0);
  const imageUrl = imageUrls[imageIndex] || imageUrls[0] || null;
  const catColor = colorForCategory(item.category || "Другое");
  const changeImage = (offset) => {
    setImageIndex((current) => (current + offset + imageUrls.length) % imageUrls.length);
  };
  return (
    <Card
      className={fullWidth ? "sf-card" : "sf-mini-card"}
      sx={{
        borderTop: `3px solid ${catColor}`,
        borderRadius: 2,
        display: "flex",
        flexDirection: "column",
        width: fullWidth ? "100%" : undefined,
        height: "100%",
      }}
    >
      {imageUrl ? (
        <Box sx={{ position: "relative", height: 180, bgcolor: "#f5f3ef" }}>
          <CardMedia
            component="img"
            image={imageUrl}
            alt={item.name}
            sx={{ width: "100%", height: "100%", objectFit: "contain" }}
          />
          {imageUrls.length > 1 && (
            <>
              <IconButton
                size="small"
                aria-label={`Предыдущее фото ${item.name}`}
                onClick={() => changeImage(-1)}
                sx={{ position: "absolute", left: 6, top: "50%", transform: "translateY(-50%)", bgcolor: "rgba(255,255,255,.9)", "&:hover": { bgcolor: "#fff" } }}
              ><ArrowBackIosNewIcon fontSize="inherit" /></IconButton>
              <IconButton
                size="small"
                aria-label={`Следующее фото ${item.name}`}
                onClick={() => changeImage(1)}
                sx={{ position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)", bgcolor: "rgba(255,255,255,.9)", "&:hover": { bgcolor: "#fff" } }}
              ><ArrowForwardIosIcon fontSize="inherit" /></IconButton>
              <Chip size="small" label={`${imageIndex + 1}/${imageUrls.length}`} sx={{ position: "absolute", right: 8, bottom: 8, bgcolor: "rgba(255,255,255,.92)" }} />
            </>
          )}
        </Box>
      ) : (
        <Box
          sx={{
            height: 180,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: `linear-gradient(135deg, ${catColor}22 0%, ${catColor}0d 100%)`,
          }}
        >
          <StorefrontIcon sx={{ fontSize: 36, color: catColor, opacity: 0.6 }} />
        </Box>
      )}
      <CardContent sx={{ p: 1.5, flexGrow: 1, display: "flex", flexDirection: "column", "&:last-child": { pb: 1.5 } }}>
        <Typography component="a" href={`/catalog/${item.showcase_item_id}`} variant="body2" fontWeight={700} sx={{ minHeight: 42, color: 'inherit', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }} title={item.name}>
          {item.name}
        </Typography>
        <Box sx={{ mt: "auto" }}>
          <Button component="a" href={`/catalog/${item.showcase_item_id}`} size="small" sx={{ px: 0 }}>Описание и характеристики</Button>
          <Button component={RouterLink} to={`/my?tab=chat&support_product=${item.showcase_item_id}&product_name=${encodeURIComponent(item.name)}`} size="small" sx={{ px: 0 }}>
            Спросить о товаре
          </Button>
          {item.min_price != null && (
            <Typography variant="body2" fontWeight={700} sx={{ color: catColor, mt: 0.5 }}>
              от {item.min_price} ₽
            </Typography>
          )}
          <Chip
            size="small"
            color={item.is_available ? "success" : "default"}
            label={item.is_available ? (item.quantity != null ? `В наличии: ${item.quantity}` : "В наличии") : "Нет"}
            sx={{ my: 0.75, fontWeight: 600 }}
          />
          <Button
            fullWidth
            size="small"
            variant="contained"
            disabled={!item.is_available}
            onClick={() => onRent(item)}
            sx={{ borderRadius: 1.5, textTransform: "none", background: BRAND_GRADIENT }}
          >
            {cartQuantity ? `Добавить ещё (${cartQuantity})` : "Взять в аренду"}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}

/* ── Блок одного пункта проката: заголовок + горизонтальная карусель товаров ── */
function PointSection({ point, items, loading, onOpenDetails, onRent, cartQuantity, sectionRef }) {
  const scrollRef = useRef(null);
  const scrollBy = (dir) => {
    scrollRef.current?.scrollBy({ left: dir * 340, behavior: "smooth" });
  };

  return (
    <div ref={sectionRef} style={{ scrollMarginTop: 108 }}>
      <Reveal style={{ marginBottom: 32 }}>
        <Box
          sx={{
            bgcolor: "#fff",
            borderRadius: 4,
            p: { xs: 2, md: 3 },
            border: "1px solid #E2E8F0",
            boxShadow: "0 10px 30px -22px rgba(15, 23, 42, 0.35)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              mb: 2,
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: "50%",
                  background: BRAND_GRADIENT,
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <PlaceIcon />
              </Box>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  {point.name}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.6 }}>
                  {point.address}
                  {point.working_hours ? ` · ${point.working_hours}` : ""}
                </Typography>
              </Box>
            </Box>
            <Button
              onClick={() => onOpenDetails(point)}
              endIcon={<ArrowForwardIcon />}
              sx={{ textTransform: "none", fontWeight: 600, whiteSpace: "nowrap", color: "#b54c27" }}
            >
              Подробнее о пункте
            </Button>
          </Box>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : items.length === 0 ? (
            <Alert severity="info">На этом пункте пока нет товаров на витрине.</Alert>
          ) : (
            <Box sx={{ position: "relative" }}>
              <IconButton aria-label="Предыдущие товары" className="sf-carousel-arrow sf-carousel-arrow--left" onClick={() => scrollBy(-1)} size="small">
                <ArrowBackIosNewIcon fontSize="small" />
              </IconButton>
              <Box ref={scrollRef} className="sf-carousel">
                {items.map((item) => (
                  <Box key={item.showcase_item_id} className="sf-carousel-item">
                    <ProductMiniCard item={item} onRent={onRent} cartQuantity={cartQuantity(item.showcase_item_id)} />
                  </Box>
                ))}
              </Box>
              <IconButton aria-label="Следующие товары" className="sf-carousel-arrow sf-carousel-arrow--right" onClick={() => scrollBy(1)} size="small">
                <ArrowForwardIosIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
        </Box>
      </Reveal>
    </div>
  );
}

/* ── Плавающий FAQ-помощник: заранее заготовленные ответы, без реального ИИ-бэкенда ── */
function RentalAssistant() {
  const [open, setOpen] = useState(false);
  const { items: faqItems, loading, error } = useSupportFaq(open);
  const [messages, setMessages] = useState([
    { from: "bot", text: "Привет! Я помощник по прокату. Выберите вопрос ниже — отвечу сразу." },
  ]);

  const ask = (faq) => {
    setMessages((prev) => [...prev, { from: "user", text: faq.q }, { from: "bot", text: faq.a }]);
  };

  return (
    <>
      <Box component="button" type="button" className="sf-assistant-fab" onClick={() => setOpen((v) => !v)} aria-expanded={open} aria-label="Помощник по прокату">
        <SmartToyIcon sx={{ fontSize: 28 }} />
      </Box>
      {open && (
        <Box className="sf-assistant-panel">
          <Box className="sf-assistant-header">
            <Typography sx={{ fontWeight: 700, fontSize: 14 }}>Помощник по прокату</Typography>
            <IconButton aria-label="Закрыть помощника" size="small" onClick={() => setOpen(false)} sx={{ color: "#fff" }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box className="sf-assistant-messages">
            {messages.map((m, idx) => (
              <Box key={idx} className={`sf-assistant-bubble sf-assistant-bubble--${m.from}`}>
                {m.text}
              </Box>
            ))}
          </Box>
          <Box className="sf-assistant-quick">
            {loading && <Typography variant="body2">Загружаем вопросы…</Typography>}
            {error && <Typography variant="body2">Не удалось загрузить ответы.</Typography>}
            {!loading && !error && faqItems.filter(item => ['checkout', 'deposit', 'extension', 'pickup'].includes(item.id)).map((faq) => (
              <Chip key={faq.q} label={faq.q} size="small" onClick={() => ask(faq)} className="sf-assistant-chip" />
            ))}
            <Button component={RouterLink} to="/help" size="small">Все вопросы и ответы</Button>
          </Box>
        </Box>
      )}
    </>
  );
}

function StorefrontPage({ mobile = false }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [portalToken] = useState(() => sessionStorage.getItem("clientPortalToken") || "");
  const [editOrder, setEditOrder] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem("storefrontEditOrder") || "null"); }
    catch { return null; }
  });
  const [cart, setCart] = useState(() => {
    try {
      const saved = JSON.parse(sessionStorage.getItem("storefrontCart") || "[]");
      return Array.isArray(saved) ? saved.map((row) => ({ ...row, weeks: row.weeks || 1 })) : [];
    }
    catch { return []; }
  });
  const [cartOpen, setCartOpen] = useState(() => searchParams.get("checkout") === "1");
  const [commonTerm, setCommonTerm] = useState(() => {
    try {
      const saved = JSON.parse(sessionStorage.getItem("storefrontCart") || "[]");
      return new Set(saved.map((row) => row.weeks || 1)).size <= 1;
    } catch { return true; }
  });
  const [commonWeeks, setCommonWeeks] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem("storefrontCart") || "[]")[0]?.weeks || 1; }
    catch { return 1; }
  });
  const [cartSubmitting, setCartSubmitting] = useState(false);
  const [cartError, setCartError] = useState("");
  const [points, setPoints] = useState([]);
  const [itemsByPoint, setItemsByPoint] = useState({});
  const [loadingItemsFor, setLoadingItemsFor] = useState({});
  const [loadingPoints, setLoadingPoints] = useState(true);
  const [error, setError] = useState(null);
  const [modalPoint, setModalPoint] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const catalogRef = useRef(null);
  const pointRefs = useRef({});
  const restoreItemName = useRef(searchParams.get("restoreItem"));
  const requestedItemId = useRef(searchParams.get("rentItem"));
  const restoreHandled = useRef(false);

  useEffect(() => { sessionStorage.setItem("storefrontCart", JSON.stringify(cart)); }, [cart]);

  useEffect(() => {
    const catalogItems = Object.values(itemsByPoint).flat();
    if (!catalogItems.length) return;
    setCart((current) => current.map((row) => {
      const catalogItem = catalogItems.find((item) => item.showcase_item_id === row.showcase_item_id);
      if (!catalogItem) return row;
      const point = points.find((item) => item.id === catalogItem.rental_point_id);
      return {
        ...row,
        rental_point_id: catalogItem.rental_point_id,
        available_quantity: catalogItem.quantity,
        min_price: catalogItem.min_price,
        point_name: point?.name,
        point_address: point?.address,
      };
    }));
  }, [itemsByPoint, points]);

  const requestVersion = useRef(0);
  const loadItemsForPoint = useCallback((pointId, version) => {
    setLoadingItemsFor((prev) => ({ ...prev, [pointId]: true }));
    api.get("/showcase/public/items", { params: { rental_point_id: pointId } })
      .then((res) => {
        if (version !== requestVersion.current) return;
        if (res.data != null && !Array.isArray(res.data)) throw new Error("Invalid catalog response");
        setItemsByPoint((prev) => ({ ...prev, [pointId]: (res.data || []).filter(Boolean) }));
      })
      .catch(() => { if (version === requestVersion.current) setError("Не удалось загрузить товары"); })
      .finally(() => { if (version === requestVersion.current) setLoadingItemsFor((prev) => ({ ...prev, [pointId]: false })); });
  }, []);

  useEffect(() => {
    const version = ++requestVersion.current;
    api.get("/showcase/public/points")
      .then((res) => {
        if (version !== requestVersion.current) return;
        if (res.data != null && !Array.isArray(res.data)) throw new Error("Invalid pickup points response");
        const data = (res.data || []).filter(Boolean);
        setPoints(data);
        data.forEach((p) => loadItemsForPoint(p.id, version));
      })
      .catch(() => { if (version === requestVersion.current) setError("Не удалось загрузить пункты проката"); })
      .finally(() => { if (version === requestVersion.current) setLoadingPoints(false); });
    return () => { requestVersion.current += 1; };
  }, [loadItemsForPoint]);

  const handleRent = (item) => {
    const point = points.find((row) => row.id === item.rental_point_id);
    setCart((current) => {
      const existing = current.find((row) => row.showcase_item_id === item.showcase_item_id);
      const limit = item.quantity == null ? 20 : item.quantity;
      if (existing) return current.map((row) => row.showcase_item_id === item.showcase_item_id
        ? { ...row, quantity: Math.min(limit, row.quantity + 1) } : row);
      return [...current, {
        showcase_item_id: item.showcase_item_id, rental_point_id: item.rental_point_id,
        name: item.name, quantity: 1, available_quantity: item.quantity,
        weeks: commonWeeks,
        min_price: item.min_price, point_name: point?.name, point_address: point?.address,
      }];
    });
    setModalPoint(null);
    setCartOpen(true);
  };

  useEffect(() => {
    if (restoreHandled.current || (!restoreItemName.current && !requestedItemId.current)) return;
    const wanted = (restoreItemName.current || '').trim().toLowerCase();
    const restored = Object.values(itemsByPoint).flat().find(
      (item) => item?.is_available && (requestedItemId.current
        ? String(item.showcase_item_id) === requestedItemId.current
        : String(item.name || "").trim().toLowerCase() === wanted)
    );
    if (!restored) {
      if (requestedItemId.current && !loadingPoints && points.every(point => Object.prototype.hasOwnProperty.call(itemsByPoint, point.id))) {
        restoreHandled.current = true;
        setError('Этот товар сейчас недоступен. Выберите другую позицию в каталоге.');
        navigate('/', { replace: true });
      }
      return;
    }
    restoreHandled.current = true;
    const point = points.find((row) => row.id === restored.rental_point_id);
    setCart((current) => current.some((row) => row.showcase_item_id === restored.showcase_item_id) ? current : [...current, {
      showcase_item_id: restored.showcase_item_id, rental_point_id: restored.rental_point_id,
      name: restored.name, quantity: 1, available_quantity: restored.quantity, weeks: commonWeeks,
      min_price: restored.min_price, point_name: point?.name, point_address: point?.address,
    }]);
    setCartOpen(true);
    navigate("/", { replace: true });
  }, [itemsByPoint, points, commonWeeks, navigate, loadingPoints]);

  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);
  const cartGroups = useMemo(() => {
    const groups = {};
    cart.forEach((item) => {
      if (!groups[item.rental_point_id]) groups[item.rental_point_id] = [];
      groups[item.rental_point_id].push(item);
    });
    return groups;
  }, [cart]);
  const cartQuantity = (showcaseItemId) => cart.find((row) => row.showcase_item_id === showcaseItemId)?.quantity || 0;
  const setItemQuantity = (showcaseItemId, next) => setCart((current) => current
    .map((row) => row.showcase_item_id === showcaseItemId ? { ...row, quantity: Math.max(0, Math.min(row.available_quantity ?? 20, next)) } : row)
    .filter((row) => row.quantity > 0));
  const setItemWeeks = (showcaseItemId, weeks) => setCart((current) => current.map(
    (row) => row.showcase_item_id === showcaseItemId ? { ...row, weeks } : row
  ));
  const applyCommonWeeks = (weeks) => {
    setCommonWeeks(weeks);
    setCart((current) => current.map((row) => ({ ...row, weeks })));
  };
  const toggleCommonTerm = (enabled) => {
    setCommonTerm(enabled);
    if (enabled) setCart((current) => current.map((row) => ({ ...row, weeks: commonWeeks })));
  };
  const checkoutCart = async () => {
    if (!cart.length || cartSubmitting) return;
    if (!portalToken) { navigate("/my?cart=1"); return; }
    try {
      setCartSubmitting(true); setCartError("");
      const { data } = await api.post("/client-portal/cart/checkout", {
        token: portalToken,
        items: cart.map(({ showcase_item_id, quantity, weeks }) => ({ showcase_item_id, quantity, weeks: weeks || 1 })),
        ...(editOrder?.deal_id ? { replace_deal_id: editOrder.deal_id } : {}),
      });
      const orders = data.orders || [];
      if (!orders.length) throw new Error("Empty checkout");
      setCart([]); sessionStorage.removeItem("storefrontCart"); sessionStorage.removeItem("storefrontEditOrder");
      navigate(`/my?order=${encodeURIComponent(orders[0].contract_token)}`);
    } catch (err) {
      setCartError(err.response?.data?.detail || "Не удалось оформить корзину. Обновите наличие и попробуйте ещё раз.");
    } finally { setCartSubmitting(false); }
  };

  const scrollToCatalog = () => {
    catalogRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const scrollToPoint = (pointId) => {
    pointRefs.current[pointId]?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Скрываем товары не в наличии по всей витрине, дальше при поиске отсеиваем по названию/описанию/категории.
  const normalizedQuery = searchQuery.trim().toLowerCase();

  const visibleItemsByPoint = useMemo(() => {
    const result = {};
    for (const p of points) {
      result[p.id] = (itemsByPoint[p.id] || [])
        .filter((item) => item.is_available)
        .filter((item) => matchesQuery(item, normalizedQuery));
    }
    return result;
  }, [points, itemsByPoint, normalizedQuery]);

  const visiblePoints = useMemo(() => {
    const selected = mobile ? searchParams.get('point') : null;
    const candidates = selected ? points.filter(point => String(point.id) === selected) : points;
    if (!normalizedQuery) return candidates;
    return candidates.filter((p) => (visibleItemsByPoint[p.id] || []).length > 0);
  }, [points, visibleItemsByPoint, normalizedQuery, mobile, searchParams]);

  const modalGroups = useMemo(
    () => (modalPoint ? groupByCategory((itemsByPoint[modalPoint.id] || []).filter((i) => i.is_available)) : {}),
    [modalPoint, itemsByPoint]
  );

  return (
    <Box className="sf-page">
      <header className="sf-header">
        <Container maxWidth="lg" className="sf-header-inner">
          <RouterLink to="/" className="sf-logo" aria-label="ПрокатПрост — главная">
            <span className="sf-logo-mark"><StorefrontIcon /></span>
            Прокат<span>Прост</span><span className="sf-logo-dot">.</span>
          </RouterLink>
          <nav className="sf-nav" aria-label="Главная навигация">
            <Button onClick={scrollToCatalog}>Каталог</Button>
            <Button href="/catalog/">Все товары</Button>
            <Button href="#how-it-works">Как это работает</Button>
            <Button href="#about">О нас</Button>
            <Button component={RouterLink} to="/help">Помощь</Button>
          </nav>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button className="sf-account" startIcon={<ShoppingCartIcon />} onClick={() => setCartOpen(true)} aria-label={`Корзина, товаров: ${cartCount}`}>
              Корзина{cartCount ? ` (${cartCount})` : ""}
            </Button>
            <Button component={RouterLink} to="/my" className="sf-account" startIcon={<PersonOutlineIcon />}>Личный кабинет</Button>
          </Box>
        </Container>
      </header>

      <main>
      {!mobile && <section className="sf-hero">
        <Container maxWidth="lg" className="sf-hero-grid">
          <div className="sf-hero-copy">
            <span className="sf-eyebrow"><span /> ПРОКАТ БЕЗ ЛИШНИХ ЗАБОТ</span>
            <h1>Нужно на время?<br /><span>Просто возьмите<br />напрокат.</span></h1>
            <p className="sf-hero-description">Медицинские товары для дома, восстановления и активной жизни. Берите на нужный срок — без покупки и лишнего хранения.</p>
            <div className="sf-hero-actions">
              <Button variant="contained" onClick={scrollToCatalog} endIcon={<ArrowForwardIcon />}>Выбрать вещь</Button>
              <Button href="#how-it-works">Как взять в аренду <span aria-hidden="true">↗</span></Button>
            </div>
            <div className="sf-hero-assurances">
              <span><CheckIcon /> Без залога</span>
              <span><CheckIcon /> Оплата онлайн</span>
              <span><CheckIcon /> Продление в личном кабинете</span>
            </div>
          </div>
          <div className="sf-hero-art sf-photo-art">
            <img src="/images/rental-locker-hero.png" alt="Постамат ПрокатПрост с медицинскими товарами и кодом получения в телефоне" fetchPriority="high" />
            <div className="sf-locker-label"><span className="sf-tag-check"><Inventory2OutlinedIcon /></span><div><b>Выбрать. Забрать. Вернуть.</b><span>Аренда в ритме вашей жизни</span></div></div>
          </div>
        </Container>
      </section>}
      <Container maxWidth="lg">
        <div className="sf-benefits">
          <div><CreditCardOffIcon /><span><b>Без замороженных денег</b><small>Платите только за аренду</small></span></div>
          <div><PhoneIphoneIcon /><span><b>Всё под рукой</b><small>Заказы и продление в личном кабинете</small></span></div>
          <div><PlaceIcon /><span><b>Удобно забрать</b><small>Выберите свой пункт проката</small></span></div>
        </div>
      </Container>

      {/* ═══════════ КАТАЛОГ ═══════════ */}
      <Container id="catalog" className="sf-catalog" maxWidth="lg" sx={{ py: { xs: 4, md: 7 } }} ref={catalogRef}>
        <Reveal>
          {!mobile && <span className="sf-eyebrow">НАЙДИТЕ ТО, ЧТО НУЖНО</span>}
          <Typography component="h2" variant="h4" sx={{ fontWeight: 700, mt: 1.5, mb: 1 }}>
            {mobile ? 'Каталог' : 'Ваши планы. Наши вещи.'}
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.7, mb: 2 }}>
            Наличие показано отдельно по каждому пункту проката.
          </Typography>
          <TextField
            fullWidth
            placeholder="Что хотите взять напрокат?"
            inputProps={{ "aria-label": "Поиск по каталогу" }}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ mb: 3, bgcolor: "#fff", "& .MuiOutlinedInput-root": { borderRadius: 2.5 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ opacity: 0.5 }} />
                </InputAdornment>
              ),
            }}
          />
        </Reveal>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loadingPoints ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : points.length === 0 ? (
          <Alert severity="info">Пока нет доступных пунктов проката с товарами на витрине.</Alert>
        ) : (
          <>
            <div className="sf-point-selector" aria-label="Пункты проката">
              <span><PlaceIcon /> Где забрать</span>
              {mobile && searchParams.get('point') && <button type="button" onClick={() => navigate('/')}>Все пункты</button>}
              {points.map((point) => (
                <button type="button" key={point.id} onClick={() => mobile ? navigate(`/?point=${point.id}`) : scrollToPoint(point.id)}>
                  {point.address || point.name}<ArrowForwardIcon />
                </button>
              ))}
            </div>

            {visiblePoints.length === 0 ? (
              <Alert severity="info">По запросу «{searchQuery}» ничего не найдено.</Alert>
            ) : (
              visiblePoints.map((point) => (
                <PointSection
                  key={point.id}
                  point={point}
                  items={visibleItemsByPoint[point.id] || []}
                  loading={!!loadingItemsFor[point.id]}
                  onOpenDetails={setModalPoint}
                  onRent={handleRent}
                  cartQuantity={cartQuantity}
                  sectionRef={(el) => (pointRefs.current[point.id] = el)}
                />
              ))
            )}
          </>
        )}
      </Container>

      <RentalAssistant />

      {/* ═══════════ МОДАЛКА ПУНКТА: КАРТА + ИНФО + ВЕСЬ АССОРТИМЕНТ ═══════════ */}
      <Dialog open={!!modalPoint} onClose={() => setModalPoint(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontWeight: 700 }}>
          {modalPoint?.name}
          <IconButton aria-label="Закрыть сведения о пункте" onClick={() => setModalPoint(null)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={0.5} sx={{ mb: 3 }}>
            {modalPoint?.address && (
              <Typography variant="body2">
                <b>Адрес:</b> {modalPoint.address}
              </Typography>
            )}
            {modalPoint?.working_hours && (
              <Typography variant="body2">
                <b>Время работы:</b> {modalPoint.working_hours}
              </Typography>
            )}
          </Stack>
          <Divider sx={{ mb: 3 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
            Весь ассортимент
          </Typography>
          {Object.keys(modalGroups).length === 0 && <Alert severity="info">Сейчас нет доступных для аренды товаров.</Alert>}
          {Object.entries(modalGroups).map(([category, groupItems]) => {
            const catColor = colorForCategory(category);
            return (
              <Box key={category} sx={{ mb: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: catColor }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {category}
                  </Typography>
                </Box>
                <Grid container spacing={1.5}>
                  {groupItems.map((item) => (
                    <Grid item xs={6} sm={4} key={item.showcase_item_id}>
                      <ProductMiniCard item={item} onRent={handleRent} cartQuantity={cartQuantity(item.showcase_item_id)} fullWidth />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            );
          })}
        </DialogContent>
      </Dialog>

      <Dialog open={cartOpen} onClose={() => !cartSubmitting && setCartOpen(false)} maxWidth="sm" fullWidth PaperProps={{ className: "sf-cart-paper" }}>
        <DialogTitle className="sf-cart-title">
          <Box><span className="sf-cart-eyebrow">ВАШ ВЫБОР</span><Typography component="span" variant="h5">Корзина</Typography></Box>
          <IconButton aria-label="Закрыть корзину" onClick={() => setCartOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent className="sf-cart-content">
          {!cart.length ? <Alert severity="info">Корзина пока пуста.</Alert> : <>
            {editOrder?.deal_id && <Alert severity="info" sx={{ mb: 2 }}>
              Вы изменяете заявку {editOrder.deal_number}. После сохранения её номер останется прежним.
              <Box sx={{ mt: 1 }}>Чтобы оформить отдельный заказ, выйдите из изменения. Прежняя заявка при этом не отменяется.</Box>
              <Button disabled={cartSubmitting} onClick={() => {
                sessionStorage.removeItem("storefrontEditOrder");
                setEditOrder(null);
                setCartError("");
              }}>Выйти из изменения заявки</Button>
            </Alert>}
            <div className="sf-cart-note"><PlaceIcon /><span>Для каждого пункта выдачи создадим отдельный заказ, оплату и код получения.</span></div>
            <Box className="sf-cart-term-mode">
              <Box><ScheduleIcon /><span><b>Срок аренды</b><small>{commonTerm ? "Один срок для всех товаров" : "Свой срок для каждого товара"}</small></span></Box>
              <FormControlLabel control={<Switch checked={commonTerm} onChange={(event) => toggleCommonTerm(event.target.checked)} />} label="Один для всех" labelPlacement="start" />
            </Box>
            {commonTerm && <TextField className="sf-cart-common-term" select fullWidth label="Общий срок" value={commonWeeks} onChange={(event) => applyCommonWeeks(Number(event.target.value))} SelectProps={{ native: true }}>
              {TERM_OPTIONS.map((weeks) => <option key={weeks} value={weeks}>{weeks} нед.</option>)}
            </TextField>}
            {Object.entries(cartGroups).map(([pointId, group]) => <Box key={pointId} className="sf-cart-point">
              <div className="sf-cart-point-heading"><PlaceIcon /><span><small>ПУНКТ ВЫДАЧИ</small><b>{group[0].point_address || group[0].point_name || `Пункт ${pointId}`}</b></span></div>
              {group.map((item) => <Box key={item.showcase_item_id} className="sf-cart-row">
                <Box className="sf-cart-product"><Typography fontWeight={700}>{item.name}</Typography>{item.min_price != null && <Typography variant="caption">от {item.min_price} ₽</Typography>}</Box>
                <Stack className="sf-cart-quantity" direction="row" alignItems="center" spacing={0.5}>
                  <IconButton size="small" aria-label={`Уменьшить ${item.name}`} onClick={() => setItemQuantity(item.showcase_item_id, item.quantity - 1)}><RemoveIcon /></IconButton>
                  <Typography aria-label={`Количество ${item.name}`}>{item.quantity}</Typography>
                  <IconButton size="small" aria-label={`Увеличить ${item.name}`} onClick={() => setItemQuantity(item.showcase_item_id, item.quantity + 1)}><AddIcon /></IconButton>
                </Stack>
                {!commonTerm && <TextField className="sf-cart-item-term" size="small" select label={`Срок для ${item.name}`} value={item.weeks || 1} onChange={(event) => setItemWeeks(item.showcase_item_id, Number(event.target.value))} SelectProps={{ native: true }}>
                  {TERM_OPTIONS.map((weeks) => <option key={weeks} value={weeks}>{weeks} нед.</option>)}
                </TextField>}
                {commonTerm && <div className="sf-cart-term-badge"><ScheduleIcon />{item.weeks || commonWeeks} нед.</div>}
                <IconButton className="sf-cart-delete" size="small" aria-label={`Удалить ${item.name}`} onClick={() => setItemQuantity(item.showcase_item_id, 0)}><DeleteOutlineIcon /></IconButton>
              </Box>)}
            </Box>)}
            {cartError && <Alert severity="error" sx={{ mt: 2 }}>{cartError}</Alert>}
          </>}
        </DialogContent>
        <DialogActions className="sf-cart-actions">
          <Button onClick={() => setCartOpen(false)}>Продолжить выбор</Button>
          <Button variant="contained" disabled={!cart.length || cartSubmitting} onClick={checkoutCart}>
            {cartSubmitting ? <CircularProgress color="inherit" size={20} /> : portalToken ? (editOrder?.deal_id ? "Сохранить заявку" : "Оформить заказы") : "Войти и оформить"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ═══════════ КАК ЭТО РАБОТАЕТ ═══════════ */}
      <Box component="section" id="how-it-works" className="sf-how" sx={{ py: { xs: 5, md: 8 } }}>
        <Container maxWidth="lg">
          <Reveal>
            <Typography component="h2" variant="h4" sx={{ fontWeight: 700, mb: 5 }}>
              Как это работает
            </Typography>
          </Reveal>
          <Grid container spacing={3}>
            {HOW_IT_WORKS.map((step, idx) => (
              <Grid item xs={12} sm={6} md={3} key={idx}>
                <Reveal delay={idx * 100}>
                  <Box sx={{ textAlign: "center", px: 1 }}>
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: "50%",
                        background: "#f5e6dc",
                        color: "#b64b28",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mx: "auto",
                        mb: 2,
                        boxShadow: "0 10px 22px -10px rgba(15, 23, 42, 0.35)",
                      }}
                    >
                      {step.icon}
                    </Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                      {idx + 1}. {step.title}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.7 }}>
                      {step.text}
                    </Typography>
                  </Box>
                </Reveal>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ═══════════ О НАС ═══════════ */}
      <Container id="about" maxWidth="lg" className="sf-about" sx={{ py: { xs: 5, md: 8 } }}>
        <Reveal>
          <span className="sf-eyebrow">ОДИН СЕРВИС. МНОГО ВОЗМОЖНОСТЕЙ.</span>
          <Typography component="h2" variant="h4" sx={{ fontWeight: 700, mt: 2, mb: 2 }}>
            Нужные вещи — на нужный срок.
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.8, lineHeight: 1.8 }}>
            Не каждую вещь нужно покупать. В ПрокатПрост можно выбрать то, что нужно сейчас,
            взять на время и вернуть после использования. Доступные товары и пункты выдачи —
            в каталоге, ваши заказы — в личном кабинете.
          </Typography>
        </Reveal>
      </Container>

      </main>
      {/* ═══════════ ФУТЕР ═══════════ */}
      <Box component="footer" sx={{ bgcolor: "#192b3e", color: "rgba(255,255,255,0.6)", py: 3 }}>
        <Container maxWidth="lg">
          <LegalLinks />
          <Divider sx={{ borderColor: "rgba(255,255,255,0.1)", mb: 2 }} />
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1.5 }}>
            <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
              © {new Date().getFullYear()} ПрокатПрост
            </Typography>
            <Stack direction="row" spacing={2} flexWrap="wrap">
              <Button size="small" component={RouterLink} to="/help" sx={{ color: 'inherit', textTransform: 'none', fontSize: '0.8rem' }}>
                Помощь и инструкции
              </Button>
              {!mobile && <Button size="small" component={RouterLink} to="/app" startIcon={<PhoneIphoneIcon />} sx={{ color: 'inherit', textTransform: 'none', fontSize: '0.8rem' }}>
                Приложение Android
              </Button>}
              <Button size="small" component={RouterLink} to="/docs/offer" sx={{ color: "inherit", textTransform: "none", fontSize: "0.8rem" }}>
                Оферта
              </Button>
              <Button size="small" component={RouterLink} to="/docs/privacy" sx={{ color: "inherit", textTransform: "none", fontSize: "0.8rem" }}>
                Конфиденциальность
              </Button>
              <Button size="small" component={RouterLink} to="/my" sx={{ color: "inherit", textTransform: "none", fontSize: "0.8rem" }}>
                Личный кабинет
              </Button>
              <Button size="small" component={RouterLink} to="/login" sx={{ color: "inherit", textTransform: "none", fontSize: "0.8rem", opacity: 0.6 }}>
                Вход для сотрудников
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}

export default StorefrontPage;
